import React, { useCallback, useState } from 'react';
import { Label, Textarea } from '@nimbus-ds/components';
import {
  GenerateCategoryDescriptionResponseDto,
  LanguageType,
} from '@tiendanube/common';
import { AiFeature } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import AiGenerationModal, {
  GenerateContentParams,
} from 'commons/components/AiGenerationModal';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { useGenerateCategoryDescription } from '../../../../hooks';

export interface AICategoryDescriptionGeneratorProps {
  onDismiss: () => void;
  onUseDescription: (description: string) => void;
  show?: boolean;
  category: {
    id: string;
    name?: string;
    lang: LanguageType;
  };
}

function CategoryDescriptionPreview({
  description,
}: Readonly<GenerateCategoryDescriptionResponseDto>) {
  // Convert plain text to HTML for preview display
  const displayHtml = description
    .split('\n\n')
    .map((paragraph) => `<p>${paragraph.trim()}</p>`)
    .join('');

  return <AiGenerationModal.HtmlPreview html={displayHtml} />;
}

function AICategoryDescriptionGenerator({
  onDismiss,
  onUseDescription,
  show = false,
  category,
}: Readonly<AICategoryDescriptionGeneratorProps>) {
  const t = useTranslationCatalog();
  const [categoryKeywords, setCategoryKeywords] = useState('');

  const { generateDescription } = useGenerateCategoryDescription();

  const handleGenerateDescription = useCallback(
    async ({ tone, wordCount }: GenerateContentParams) => {
      const response = await generateDescription({
        category_id: category.id,
        category_name: category.name || '',
        lang: category.lang,
        keywords: categoryKeywords,
        tone,
        word_count: wordCount,
      });

      return {
        ...response,
        run_id: response.run_id || response.session_id || '',
      };
    },
    [category, categoryKeywords, generateDescription],
  );

  const handleChangeKeywords = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCategoryKeywords(e.target.value);
  };

  const handleUseDescriptionClick = ({
    description,
  }: GenerateCategoryDescriptionResponseDto) => {
    // Use the plain text description directly from the API
    onUseDescription(description);
  };

  const moveCursorToEnd = (
    e:
      | React.FocusEvent<HTMLTextAreaElement, Element>
      | React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    e.currentTarget.setSelectionRange(
      e.currentTarget.value.length,
      e.currentTarget.value.length,
    );
  };

  return (
    <AiGenerationModal
      show={show}
      feature={AiFeature.AI_CATEGORIES_DESCRIPTION}
      insufficientInfo={false}
      disabledGenerateCta={!categoryKeywords}
      onDismiss={onDismiss}
      onGenerateContent={handleGenerateDescription}
      onUseContent={handleUseDescriptionClick}
      feedbackTitle={t(
        'categories.aiDescriptionGenerator.categoryDescriptionFeedbackModal.title',
      )}
      feedbackText={t(
        'categories.aiDescriptionGenerator.categoryDescriptionFeedbackModal.text',
      )}
      showTone
      wordCountValues={{ short: 75, medium: 150, large: 300 }}
      extraControls={
        <Stack column align="stretch" spacing="tight">
          <Label htmlFor="category-keywords">
            {t(
              'categories.aiDescriptionGenerator.modal.categoryKeywords.label',
            )}
          </Label>
          <Textarea
            id="category-keywords"
            name="category-keywords"
            value={categoryKeywords}
            lines={10}
            onFocus={moveCursorToEnd}
            onChange={handleChangeKeywords}
            placeholder={t(
              'categories.aiDescriptionGenerator.modal.categoryKeywords.placeHolder',
            )}
          />
        </Stack>
      }
      previewGeneratedContent={CategoryDescriptionPreview}
    />
  );
}

export default AICategoryDescriptionGenerator;
